import { useState } from 'react'
import { headingAngle, screenConstant, type Cluster, type Point } from './geometry'

/** Longueur de coque en unités viewBox à k=1 (dessin affiné : trait fin + coque un peu réduite). */
export const FLEET_HULL_UNITS = 5

const PARCHMENT_BG = '#0c2f39'
const SAND_EDGE = '#E5CC8F'

export interface FleetMarkerData extends Point {
  /** Clé stable d'une flotte (mission / raid) pour le keying React. */
  key: string
  origin: Point
  destination: Point
  color: string
  count: number
  label: string
  destName: string
}

/**
 * Marqueur de flotte « option A » : coque vue de dessus line-art dorée, pivotée
 * vers son cap, avec un sillage en V estompé derrière la poupe. Taille écran
 * constante (contre-scale `1/k`). Une flotte (count>1) porte une pastille
 * parchemin avec le compte. Cliquable si `onSelect` est fourni.
 */
export function FleetMarker({
  marker,
  k,
  onSelect,
}: {
  marker: FleetMarkerData
  k: number
  onSelect?: (key: string) => void
}) {
  const angle = headingAngle(marker.origin, marker.destination)
  const s = screenConstant(1, k) // 1/k
  const h = FLEET_HULL_UNITS

  // Coque top-down line-art (proue +x, poupe −x) : contour ogive + quille + un banc.
  const outline =
    `M ${h / 2} 0 ` +
    `C ${h * 0.18} ${-h * 0.3} ${-h * 0.42} ${-h * 0.26} ${-h / 2} 0 ` +
    `C ${-h * 0.42} ${h * 0.26} ${h * 0.18} ${h * 0.3} ${h / 2} 0 Z`
  const keel = `M ${-h * 0.42} 0 L ${h * 0.42} 0`
  const thwart = `M ${-h * 0.12} ${-h * 0.2} L ${-h * 0.12} ${h * 0.2}`

  const interactive = Boolean(onSelect)

  return (
    <g
      data-role="fleet-marker"
      style={interactive ? { pointerEvents: 'auto', cursor: 'pointer' } : undefined}
      onClick={interactive ? () => onSelect?.(marker.key) : undefined}
    >
      <g
        data-role="fleet-hull"
        transform={`translate(${marker.x} ${marker.y}) scale(${s}) rotate(${angle})`}
        fill="none"
        stroke={SAND_EDGE}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Sillage doré estompé derrière la poupe (−x). */}
        <path data-role="fleet-wake" d={`M ${-h / 2} ${-h * 0.16} L ${-h * 1.25} ${-h * 0.5}`} strokeWidth={0.2} opacity={0.3} />
        <path data-role="fleet-wake" d={`M ${-h / 2} ${h * 0.16} L ${-h * 1.25} ${h * 0.5}`} strokeWidth={0.2} opacity={0.3} />
        <path data-role="fleet-hull-outline" d={outline} fill="none" stroke={SAND_EDGE} strokeWidth={0.2} />
        <path d={keel} strokeWidth={0.16} opacity={0.8} />
        <path d={thwart} strokeWidth={0.16} opacity={0.8} />
      </g>

      {marker.count > 1 && (
        <g data-role="fleet-badge" transform={`translate(${marker.x} ${marker.y}) scale(${s})`}>
          <circle cx={h * 0.6} cy={-h * 0.6} r={1.7} fill={PARCHMENT_BG} stroke={SAND_EDGE} strokeWidth={0.3} />
          <text x={h * 0.6} y={-h * 0.6} fontSize={2.2} fontWeight={700} fill={SAND_EDGE} textAnchor="middle" dominantBaseline="central">
            {marker.count}
          </text>
        </g>
      )}

      <title>
        {marker.label} → {marker.destName}
        {marker.count > 1 ? ` · flotte de ${marker.count}` : ''}
      </title>
    </g>
  )
}

/** Décalage radial d'un membre déplié (éventail), en unités écran-constantes. */
function fanOffset(i: number, n: number, k: number): Point {
  if (n <= 1) return { x: 0, y: 0 }
  const r = screenConstant(FLEET_HULL_UNITS * 1.1, k)
  const a = (i / n) * Math.PI * 2
  return { x: Math.cos(a) * r, y: Math.sin(a) * r }
}

/**
 * Un cluster de flottes superposées. Un seul membre → marqueur direct. Plusieurs
 * → une chip parchemin `×N` qui se déplie en éventail au survol / focus / tap.
 */
export function FleetCluster({
  cluster,
  k,
  onSelect,
}: {
  cluster: Cluster<FleetMarkerData>
  k: number
  onSelect?: (key: string) => void
}) {
  const [open, setOpen] = useState(false)
  const n = cluster.items.length

  if (n === 1) {
    return <FleetMarker marker={cluster.items[0]} k={k} onSelect={onSelect} />
  }

  if (open) {
    return (
      <g onMouseLeave={() => setOpen(false)}>
        {cluster.items.map((m, i) => {
          const off = fanOffset(i, n, k)
          return <FleetMarker key={m.key} marker={{ ...m, x: m.x + off.x, y: m.y + off.y }} k={k} onSelect={onSelect} />
        })}
      </g>
    )
  }

  const s = screenConstant(1, k)
  return (
    <g
      data-role="fleet-cluster"
      role="button"
      tabIndex={0}
      style={{ pointerEvents: 'auto', cursor: 'pointer' }}
      onMouseEnter={() => setOpen(true)}
      onFocus={() => setOpen(true)}
      onClick={() => setOpen(true)}
      transform={`translate(${cluster.x} ${cluster.y}) scale(${s})`}
    >
      <circle r={2.6} fill={PARCHMENT_BG} stroke={SAND_EDGE} strokeWidth={0.4} />
      <text fontSize={2.8} fontWeight={700} fill={SAND_EDGE} textAnchor="middle" dominantBaseline="central">
        ×{n}
      </text>
      <title>{n} flottes ici</title>
    </g>
  )
}
